import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  usePrizePayoutsQuery,
  useProcessWalletPaymentsMutation,
  useBulkUpdateStatusMutation,
  useBulkUpdateStatusByFilterMutation,
  getPrizePayouts,
  exportPrizePayoutsCSV,
  type PrizePayoutFilters,
} from '@/api/prize-payouts';
import { useGetPickemsQuery } from '@/api/pickems/getPickems';
import { useGetDailyRevealsQuery } from '@/api/daily-reveals/getDailyReveals';
import { ENABLED_GAME_TYPES } from '@/lib/platform-filters';
import { DollarSign, CreditCard, Loader2, Download } from 'lucide-react';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { useCurrentAdminUser } from '@/hooks/useCurrentAdminUser';
import { toast } from 'sonner';

export const Route = createFileRoute('/dashboard/prize-payouts/')({
  component: PrizePayoutsPage,
});

/**
 * Contest-type filter labels for this page only — historically "Pickem" (no
 * apostrophe) rather than the sports-config registry's "Pick'em" label.
 * Falls back to the registry label for any game type not listed here.
 */
const CONTEST_TYPE_FILTER_LABELS: Record<string, string> = {
  pickem: 'Pickem',
};

function PrizePayoutsPage() {
  const { canManageContests } = useCurrentAdminUser();
  const [filters, setFilters] = useState<PrizePayoutFilters>({
    paymentStatus: 'pending',
    prizeType: '',
    pickemId: '',
    gameType: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    limit: 100,
    offset: 0,
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showProcessConfirm, setShowProcessConfirm] = useState(false);
  const [showPayAllConfirm, setShowPayAllConfirm] = useState(false);
  const [showMarkPaidConfirm, setShowMarkPaidConfirm] = useState(false);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [showMarkAllPaidConfirm, setShowMarkAllPaidConfirm] = useState(false);
  const [isPayAllLoading, setIsPayAllLoading] = useState(false);
  const [isMarkAllPaidLoading, setIsMarkAllPaidLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const contestType = filters.gameType || 'all';

  const { data: payoutsData, isLoading, refetch } = usePrizePayoutsQuery(filters);
  const { data: pickems = [] } = useGetPickemsQuery();
  const { data: dailyReveals = [] } = useGetDailyRevealsQuery();
  const allContests = useMemo(
    () => [
      ...pickems.map(p => ({ id: p.pickem_id, title: p.title_en, type: 'Pickem' })),
      ...dailyReveals.map(d => ({ id: d.pickem_id, title: d.title_en, type: 'Daily Reveal' })),
    ],
    [pickems, dailyReveals]
  );
  const filteredContests = useMemo(() => {
    if (contestType === 'all') return allContests;
    if (contestType === 'pickem') return allContests.filter(c => c.type === 'Pickem');
    if (contestType === 'daily_reveal') return allContests.filter(c => c.type === 'Daily Reveal');
    if (contestType === 'squares') return []; // Squares don't appear in contest list
    return allContests;
  }, [allContests, contestType]);
  const processPaymentsMutation = useProcessWalletPaymentsMutation();
  const bulkMarkPaidMutation = useBulkUpdateStatusMutation();
  const bulkMarkPaidByFilterMutation = useBulkUpdateStatusByFilterMutation();

  // Detect "hits mode" — when filtering by daily reveal contest, type, or profit_boost/jackpot prize types
  const isDailyRevealMode = useMemo(() => {
    if (contestType === 'daily_reveal') return true;
    const dailyRevealIds = new Set(dailyReveals.map(d => d.pickem_id));
    const isFilteredByDailyReveal = !!filters.pickemId && dailyRevealIds.has(filters.pickemId);
    const isFilteredByDRPrizeType =
      filters.prizeType === 'profit_boost_token' || filters.prizeType === 'jackpot';
    return isFilteredByDailyReveal || isFilteredByDRPrizeType;
  }, [filters.pickemId, filters.prizeType, dailyReveals, contestType]);

  const rawPayouts = payoutsData?.data;
  const payouts = useMemo(() => {
    const payoutsList = rawPayouts ?? [];
    if (isDailyRevealMode) {
      // Daily Reveal: highest rank (hit tier) first.
      return [...payoutsList].sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0));
    }
    // Pick'em (and other non-DR): finishing position ascending — 1st at the top.
    // Null/0 ranks (e.g. non-cash or unranked) fall to the bottom.
    return [...payoutsList].sort((a, b) => (a.rank || Infinity) - (b.rank || Infinity));
  }, [rawPayouts, isDailyRevealMode]);
  const total = payoutsData?.total || 0;
  const totalAmount: number = (payoutsData as any)?.totalAmount || 0;

  // Compute selected payouts and their total amount for the confirm dialog
  const selectedPayouts = useMemo(() => {
    return payouts.filter(p => selectedIds.has(p.id));
  }, [payouts, selectedIds]);

  const selectedTotalAmount = useMemo(() => {
    return selectedPayouts.reduce((sum, p) => sum + parseFloat(p.prizeAmount || '0'), 0);
  }, [payouts]);

  const handleFilterChange = (key: keyof PrizePayoutFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === 'all' ? '' : value, // Convert 'all' to empty string for API
      offset: 0, // Reset to first page
    }));
  };

  const handleProcessPayments = async () => {
    if (selectedIds.size === 0) return;

    try {
      const result = await processPaymentsMutation.mutateAsync({
        ids: Array.from(selectedIds),
      });

      const { summary, results } = result;

      if (summary.succeeded === 0 && summary.failed === 0 && summary.skipped > 0) {
        const reasons = results
          .filter((r: { status: string }) => r.status === 'skipped')
          .map((r: { message?: string }) => r.message)
          .filter(Boolean);
        const uniqueReasons = [...new Set(reasons)];
        toast.error(
          `All ${summary.skipped} skipped: ${uniqueReasons.join('; ') || 'ineligible for processing'}`
        );
      } else if (summary.failed === 0) {
        toast.success(
          `Processed ${summary.succeeded} payments ($${summary.totalAmount.toFixed(2)})${summary.skipped > 0 ? ` - ${summary.skipped} skipped` : ''}`
        );
      } else {
        toast.warning(
          `${summary.succeeded} succeeded, ${summary.failed} failed${summary.skipped > 0 ? `, ${summary.skipped} skipped` : ''} ($${summary.totalAmount.toFixed(2)} total)`
        );
      }

      setSelectedIds(new Set());
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to process payments');
    }
  };

  const handlePayAllPending = async () => {
    setIsPayAllLoading(true);
    try {
      // Paginate through ALL pending payouts matching current filters
      const PAGE_SIZE = 5000;
      const allIds: string[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const page = await getPrizePayouts({
          ...filters,
          paymentStatus: 'pending',
          limit: PAGE_SIZE,
          offset,
        });
        const ids = (page.data || []).map((p: { id: string }) => p.id);
        allIds.push(...ids);
        hasMore = page.hasMore;
        offset += PAGE_SIZE;
      }

      if (allIds.length === 0) {
        toast.info('No pending payouts found matching current filters');
        return;
      }

      // Batch in chunks of 100 (backend maxItems limit)
      const BATCH_SIZE = 100;
      let totalSucceeded = 0;
      let totalFailed = 0;
      let totalSkipped = 0;
      let totalAmount = 0;

      for (let i = 0; i < allIds.length; i += BATCH_SIZE) {
        const batch = allIds.slice(i, i + BATCH_SIZE);
        try {
          const result = await processPaymentsMutation.mutateAsync({ ids: batch });
          totalSucceeded += result.summary.succeeded;
          totalFailed += result.summary.failed;
          totalSkipped += result.summary.skipped;
          totalAmount += result.summary.totalAmount;
        } catch (err: unknown) {
          totalFailed += batch.length;
          toast.error(
            `Batch ${Math.floor(i / BATCH_SIZE) + 1} failed: ${err instanceof Error ? err.message : 'Unknown error'}`
          );
        }
      }

      if (totalSucceeded === 0 && totalFailed === 0 && totalSkipped > 0) {
        toast.error(`All ${totalSkipped} skipped (ineligible for processing)`);
      } else if (totalFailed === 0) {
        toast.success(
          `Processed ${totalSucceeded} payments ($${totalAmount.toFixed(2)})${totalSkipped > 0 ? ` - ${totalSkipped} skipped` : ''}`
        );
      } else {
        toast.warning(
          `${totalSucceeded} succeeded, ${totalFailed} failed${totalSkipped > 0 ? `, ${totalSkipped} skipped` : ''} ($${totalAmount.toFixed(2)} total)`
        );
      }

      setSelectedIds(new Set());
      refetch();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to process payments');
    } finally {
      setIsPayAllLoading(false);
    }
  };

  const handlePayAllClick = () => {
    setShowPayAllConfirm(true);
  };

  const handleMarkAllPendingPaid = async () => {
    setIsMarkAllPaidLoading(true);
    try {
      const result = await bulkMarkPaidByFilterMutation.mutateAsync({
        filters: {
          paymentStatus: 'pending',
          pickemId: filters.pickemId || undefined,
          userId: filters.userId || undefined,
          prizeType: filters.prizeType || undefined,
          gameType: filters.gameType || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
        },
        status: 'paid',
      });

      if (result.updated === 0) {
        toast.info('No pending payouts found matching current filters');
      } else {
        toast.success(
          `Marked ${result.updated.toLocaleString()} payout${result.updated !== 1 ? 's' : ''} as paid`
        );
      }

      setSelectedIds(new Set());
      refetch();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to mark payouts as paid');
    } finally {
      setIsMarkAllPaidLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setIsExportLoading(true);
    try {
      await exportPrizePayoutsCSV(filters);
      toast.success('CSV export downloaded');
    } catch {
      toast.error('Failed to export CSV');
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (selectedIds.size === 0) return;

    const BATCH_SIZE = 100;
    const allIds = Array.from(selectedIds);
    let totalUpdated = 0;
    let hasError = false;

    for (let i = 0; i < allIds.length; i += BATCH_SIZE) {
      const batch = allIds.slice(i, i + BATCH_SIZE);
      try {
        const result = await bulkMarkPaidMutation.mutateAsync({ ids: batch, status: 'paid' });
        totalUpdated += result.updated;
      } catch (err: unknown) {
        hasError = true;
        toast.error(
          `Batch ${Math.floor(i / BATCH_SIZE) + 1} failed: ${err instanceof Error ? err.message : 'Unknown error'}`
        );
      }
    }

    if (totalUpdated > 0) {
      if (hasError) {
        toast.warning(
          `Marked ${totalUpdated} payout${totalUpdated !== 1 ? 's' : ''} as paid (some batches failed)`
        );
      } else {
        toast.success(`Marked ${totalUpdated} payout${totalUpdated !== 1 ? 's' : ''} as paid`);
      }
    }

    setSelectedIds(new Set());
    refetch();
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === payouts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(payouts.map(p => p.id)));
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'paid':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'processing':
        return 'outline';
      case 'failed':
      case 'cancelled':
      case 'refunded':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const handlePrevPage = () => {
    setFilters(prev => ({
      ...prev,
      offset: Math.max(0, (prev.offset || 0) - (prev.limit || 200)),
    }));
  };

  const handleNextPage = () => {
    if (payoutsData?.hasMore) {
      setFilters(prev => ({
        ...prev,
        offset: (prev.offset || 0) + (prev.limit || 200),
      }));
    }
  };

  return (
    <PageContainer>
      <Breadcrumbs overrides={{ 'prize-payouts': 'Prize Payouts' }} />

      <div className="flex gap-4 items-center mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-gray-500" />
          <h1 className="text-2xl font-semibold">Prize Payouts</h1>
        </div>

        <div className="ml-auto flex gap-2">
          <TooltipProvider>
            {selectedIds.size > 0 && canManageContests && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      className="border-blue-500 text-blue-500 hover:bg-blue-500/10"
                      onClick={() => setShowMarkPaidConfirm(true)}
                      disabled={
                        bulkMarkPaidMutation.isPending ||
                        processPaymentsMutation.isPending ||
                        isPayAllLoading
                      }
                    >
                      {bulkMarkPaidMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="mr-2 h-4 w-4" />
                      )}
                      Mark {selectedIds.size} as Paid
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Manually mark the {selectedIds.size} selected payout
                      {selectedIds.size !== 1 ? 's' : ''} as paid.
                    </p>
                    <p>No payment is processed — this is a status override only.</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => setShowProcessConfirm(true)}
                      disabled={processPaymentsMutation.isPending || isPayAllLoading}
                    >
                      {processPaymentsMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="mr-2 h-4 w-4" />
                      )}
                      Process {selectedIds.size} Payment{selectedIds.size !== 1 ? 's' : ''}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Send the {selectedIds.size} selected payout{selectedIds.size !== 1 ? 's' : ''}{' '}
                      to the Wallet API.
                    </p>
                    <p>
                      Non-cash prizes, already-paid entries, and payouts missing an SSO ID will be
                      skipped.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
            {(selectedIds.size === 0 || !canManageContests) && (
              <>
                {canManageContests && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        className="border-green-600 text-green-600 hover:bg-green-50"
                        onClick={() => setShowMarkAllPaidConfirm(true)}
                        disabled={
                          bulkMarkPaidMutation.isPending || isMarkAllPaidLoading || total === 0
                        }
                      >
                        {isMarkAllPaidLoading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <CreditCard className="mr-2 h-4 w-4" />
                        )}
                        Mark All Pending Paid
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Manually mark all pending payouts matching the current filters as paid.</p>
                      <p>
                        Acts on all {total.toLocaleString()} filtered result{total !== 1 ? 's' : ''}
                        , not just this page.
                      </p>
                      <p>No payment is processed — this is a status override only.</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {canManageContests && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        className="border-green-600 text-green-600 hover:bg-green-50"
                        onClick={handlePayAllClick}
                        disabled={
                          processPaymentsMutation.isPending || isPayAllLoading || total === 0
                        }
                      >
                        {isPayAllLoading ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <CreditCard className="mr-2 h-4 w-4" />
                        )}
                        Pay All Pending
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        Process all pending payouts matching the current filters via the Wallet API.
                      </p>
                      <p>
                        Acts on all {total.toLocaleString()} filtered result{total !== 1 ? 's' : ''}
                        , not just this page.
                      </p>
                      <p>Non-cash prizes and payouts missing an SSO ID will be skipped.</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={() => setShowExportConfirm(true)}
                      disabled={isExportLoading || total === 0}
                    >
                      {isExportLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-4 w-4" />
                      )}
                      Export CSV
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Download all {total.toLocaleString()} filtered result{total !== 1 ? 's' : ''}{' '}
                      as a CSV file.
                    </p>
                    <p>Exports all pages, not just the current view.</p>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
          </TooltipProvider>
        </div>
      </div>

      {/* Filters */}
      <ContentCard className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="space-y-2">
            <Label htmlFor="payment-status">Payment Status</Label>
            <Select
              value={filters.paymentStatus || 'all'}
              onValueChange={value => handleFilterChange('paymentStatus', value)}
            >
              <SelectTrigger id="payment-status">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="prize-type">Prize Type</Label>
            <Select
              value={filters.prizeType || 'all'}
              onValueChange={value => handleFilterChange('prizeType', value)}
            >
              <SelectTrigger id="prize-type">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="crowns">Crowns</SelectItem>
                <SelectItem value="other">Other</SelectItem>
                <SelectItem value="profit_boost_token">Profit Boost</SelectItem>
                <SelectItem value="bonus_bet">Bonus Bet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contest-type">Contest Type</Label>
            <Select
              value={contestType}
              onValueChange={value => {
                setFilters(prev => ({
                  ...prev,
                  gameType: value === 'all' ? '' : value,
                  pickemId: '',
                  offset: 0,
                }));
              }}
            >
              <SelectTrigger id="contest-type">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ENABLED_GAME_TYPES.map(gameType => (
                  <SelectItem key={gameType.code} value={gameType.code}>
                    {CONTEST_TYPE_FILTER_LABELS[gameType.code] ?? gameType.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contest">Contests</Label>
            <Select
              value={filters.pickemId || 'all'}
              onValueChange={value => handleFilterChange('pickemId', value)}
            >
              <SelectTrigger id="contest">
                <SelectValue placeholder="All Contests" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Contests</SelectItem>
                {filteredContests.map(contest => (
                  <SelectItem key={contest.id} value={contest.id}>
                    {contest.title} ({contest.type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="date-from">From Date</Label>
            <Input
              id="date-from"
              type="date"
              value={filters.dateFrom}
              onChange={e => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date-to">To Date</Label>
            <Input
              id="date-to"
              type="date"
              value={filters.dateTo}
              onChange={e => handleFilterChange('dateTo', e.target.value)}
            />
          </div>
        </div>
      </ContentCard>

      {/* Summary */}
      {payouts.length > 0 && (
        <div className="flex gap-6 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Total Transactions:</span>
            <span className="font-semibold">{total.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Total Amount:</span>
            <span className="font-semibold">
              $
              {totalAmount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>
      )}

      {/* Data Table */}
      <ContentCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={payouts.length > 0 && selectedIds.size === payouts.length}
                  onCheckedChange={toggleSelectAll}
                />
              </TableHead>
              <TableHead>Transaction ID</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>SSO User ID</TableHead>
              <TableHead>Contests</TableHead>
              <TableHead>{isDailyRevealMode ? 'Tier' : 'Rank'}</TableHead>
              <TableHead>Prize Type</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Desc</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-4" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell className="text-right">
                    <Skeleton className="h-8 w-16 ml-auto" />
                  </TableCell>
                </TableRow>
              ))
            ) : payouts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-gray-500 py-8">
                  No prize payouts found
                </TableCell>
              </TableRow>
            ) : (
              payouts.map(payout => (
                <TableRow key={payout.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(payout.id)}
                      onCheckedChange={() => toggleSelection(payout.id)}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{payout.walletReferenceId}</TableCell>
                  <TableCell className="font-medium">{payout.username}</TableCell>
                  <TableCell className="font-mono text-xs">{payout.ssoUserId || '-'}</TableCell>
                  <TableCell>
                    {payout.squaresGameId
                      ? payout.squaresGameId
                      : payout.pickemTitle && payout.contestFriendlyId != null
                        ? `${payout.pickemTitle} (#${payout.contestFriendlyId})`
                        : payout.contestFriendlyId != null
                          ? `#${payout.contestFriendlyId}`
                          : (payout.pickemTitle ?? '-')}
                  </TableCell>
                  <TableCell>
                    {payout.rank != null
                      ? isDailyRevealMode
                        ? payout.rank
                        : `#${payout.rank}`
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{payout.prizeType}</Badge>
                  </TableCell>
                  <TableCell className="font-mono">
                    {payout.prizeType === 'cash'
                      ? `$${parseFloat(payout.prizeAmount || '0').toFixed(2)}`
                      : parseFloat(payout.prizeAmount || '0').toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(payout.paymentStatus)}>
                      {payout.paymentStatus}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <TzDateDisplay
                      utcDate={payout.createdAt}
                      formatString="MMM d, yyyy h:mm a"
                      showUtcBadge={false}
                      showTimezoneTooltip={false}
                      dateOnly={false}
                    />
                  </TableCell>
                  <TableCell>
                    {payout.paymentError && (
                      <span className="text-xs text-red-500" title={payout.paymentError}>
                        {payout.paymentError}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {payouts.length > 0 && (
          <div className="flex items-center justify-between px-4 py-4 border-t">
            <div className="text-sm text-gray-500">
              Showing {(filters.offset || 0) + 1} to {(filters.offset || 0) + payouts.length} of{' '}
              {total}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevPage}
                disabled={!filters.offset || filters.offset === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={!payoutsData?.hasMore}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </ContentCard>

      <ConfirmDialog
        open={showMarkPaidConfirm}
        onOpenChange={setShowMarkPaidConfirm}
        title="Mark as Paid"
        description={`This will mark ${selectedIds.size} selected payout${selectedIds.size !== 1 ? 's' : ''} as paid. No payment will be processed — this is a manual override only.`}
        confirmLabel="Mark as Paid"
        onConfirm={handleMarkAsPaid}
      />

      <ConfirmDialog
        open={showProcessConfirm}
        onOpenChange={setShowProcessConfirm}
        title="Process Wallet Payments"
        description={`This will process ${selectedPayouts.length} payout${selectedPayouts.length !== 1 ? 's' : ''} totalling $${selectedTotalAmount.toFixed(2)} via the DraftKings Wallet API. Non-eligible payouts (non-cash, already paid, missing SSO ID) will be skipped. This action cannot be undone.`}
        confirmLabel="Process Payments"
        onConfirm={handleProcessPayments}
      />

      <ConfirmDialog
        open={showPayAllConfirm}
        onOpenChange={setShowPayAllConfirm}
        title="Pay All Pending Payouts"
        description={`This will process ALL pending payouts matching your current filters via the DraftKings Wallet API. Non-eligible payouts (non-cash, missing SSO ID) will be skipped. This action cannot be undone.`}
        confirmLabel="Pay All Pending"
        onConfirm={handlePayAllPending}
      >
        <div className="mt-4 rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-2 text-left font-medium">Total Transactions</th>
                <th className="px-4 py-2 text-right font-medium">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-2 font-semibold">{total.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-semibold">
                  $
                  {totalAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={showMarkAllPaidConfirm}
        onOpenChange={setShowMarkAllPaidConfirm}
        title="Mark All Pending Paid"
        description="This will mark ALL pending payouts matching your current filters as paid. No payment will be processed — this is a manual status override only."
        confirmLabel="Mark All Pending Paid"
        onConfirm={handleMarkAllPendingPaid}
      >
        <div className="mt-4 rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-2 text-left font-medium">Total Transactions</th>
                <th className="px-4 py-2 text-right font-medium">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-2 font-semibold">{total.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-semibold">
                  $
                  {totalAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={showExportConfirm}
        onOpenChange={setShowExportConfirm}
        title="Export Payouts to CSV"
        description="Export all payouts matching the current filters to a CSV file."
        confirmLabel="Export CSV"
        onConfirm={handleExportCSV}
      >
        <div className="mt-4 rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-2 text-left font-medium">Total Transactions</th>
                <th className="px-4 py-2 text-right font-medium">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-2 font-semibold">{total.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-semibold">
                  $
                  {totalAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ConfirmDialog>
    </PageContainer>
  );
}
